// Module-level ref for bridging GoodsShipmentForm (RHF) with the
// legacy AppRecord save flow (Ant Form + transformUpdateRecordInput).
//
// Supports multiple shipments: each GoodsShipmentForm registers by index.
// transformCustomsDeclaration.toSchema() reads all at save time.
// Route validateUpdate validates all registered shipments.
//
// Uses a snapshot pattern: form data is persisted on every change so that
// navigating between shipments preserves edits (same as goodsItemFormRef).

import type { GoodsShipmentFormState } from '../schemas/forms/goodsShipment';
import type { GoodsShipmentApiData } from './CuDeGoodsShipmentTransformer';

export interface GoodsShipmentFormRef {
  getFormData: () => GoodsShipmentFormState;
  getOriginal: () => GoodsShipmentApiData;
  /** Index of this shipment in the goodsShipment array. */
  shipmentIndex: number;
  /** Runs save-time validation. Returns true if valid, false if errors were set on the form. Only works when mounted. */
  validate: () => Promise<boolean>;
}

export interface GoodsShipmentSnapshot {
  formData: GoodsShipmentFormState;
  original: GoodsShipmentApiData;
  shipmentIndex: number;
}

// Live refs for currently-mounted shipment forms
const liveRefs = new Map<number, GoodsShipmentFormRef>();
// Snapshots for all shipments (persists across navigation)
const snapshots = new Map<number, GoodsShipmentSnapshot>();

// Register or update a live shipment form ref by index.
export const setGoodsShipmentFormRef = (newRef: GoodsShipmentFormRef) => {
  liveRefs.set(newRef.shipmentIndex, newRef);
};

// Remove a specific shipment's live ref (on unmount). Snapshot is preserved.
export const clearGoodsShipmentFormRef = (owner: GoodsShipmentFormRef) => {
  const current = liveRefs.get(owner.shipmentIndex);
  if (current === owner) liveRefs.delete(owner.shipmentIndex);
};

// Store/update a shipment snapshot (called on every form change).
export const setGoodsShipmentSnapshot = (snapshot: GoodsShipmentSnapshot) => {
  snapshots.set(snapshot.shipmentIndex, snapshot);
};

// Get a snapshot for hydrating defaultValues on re-mount.
export const getGoodsShipmentSnapshot = (
  shipmentIndex: number,
): GoodsShipmentSnapshot | undefined => snapshots.get(shipmentIndex);

// Clear all refs and snapshots (e.g. on route change).
export const clearAllGoodsShipmentFormRefs = () => {
  liveRefs.clear();
  snapshots.clear();
};

// Get all registered shipment form refs (live only — for validate).
export const getAllLiveGoodsShipmentFormRefs = (): ReadonlyMap<
  number,
  GoodsShipmentFormRef
> => liveRefs;

// Get all snapshots (for toSchema at save time).
export const getAllGoodsShipmentSnapshots = (): ReadonlyMap<
  number,
  GoodsShipmentSnapshot
> => snapshots;

/**
 * Get all registered shipment form refs.
 * @deprecated Use getAllLiveGoodsShipmentFormRefs or getAllGoodsShipmentSnapshots
 * @return {ReadonlyMap} live refs map
 */
export const getAllGoodsShipmentFormRefs = (): ReadonlyMap<
  number,
  GoodsShipmentFormRef
> => liveRefs;

// Get a single ref by index (backward compat).
export const getGoodsShipmentFormRef = (
  index?: number,
): GoodsShipmentFormRef | null => {
  if (index != null) return liveRefs.get(index) ?? null;
  const first = liveRefs.values().next();
  return first.done ? null : first.value;
};
